import { Injectable } from '@angular/core';
import { TALKJS_CONFIGURATION_SETTINGS } from '@app/utils/constants/talkjs-configuration-settings';
import { Deferred } from '@app/utils/defer/deferred.util';
import { environment } from '@environment';
import { TravelDesigner } from '@app/utils/models/itinerary/travel-designer.model';

import { User } from '@app/utils/models/user/user.model';
import Talk from 'talkjs';
import { SupportedLanguage } from '@app/utils/constants/supported-languages';
import { TALKJS_LOCALE_MAP } from '@app/utils/constants/talkjs-locale-map';

@Injectable({
  providedIn: 'root',
})
export class TalkJsService {
  private currentSessionDeferred = new Deferred<Talk.Session>();

  async initTalkJsSession(user: Talk.User, chatSignature: string): Promise<void> {
    await Talk.ready;
    const session = new Talk.Session({
      appId: environment.talkJsAppKey,
      me: user,
      signature: chatSignature,
    });
    this.currentSessionDeferred = new Deferred<Talk.Session>();
    this.currentSessionDeferred.resolve(session);
  }

  async getTalkJsSession(): Promise<Talk.Session> {
    return this.currentSessionDeferred.promise;
  }

  async createPopup(theme?: Talk.ThemeOptions): Promise<Talk.Popup | null> {
    const session = await this.currentSessionDeferred.promise;
    if (session.isAlive) {
      return session.createPopup({
        showChatHeader: true,
        showCloseInHeader: true,
        // Opening and closing belong to our own chat launcher.
        launcher: 'never',
        messageField: {
          enterSendsMessage: false,
        },
        theme,
      });
    }
    return null;
  }

  async createTalkUser(
    applicationUser: User | TravelDesigner,
    userLanguage: SupportedLanguage | null,
  ): Promise<Talk.User> {
    await Talk.ready;
    return new Talk.User({
      id: applicationUser.id,
      name: `${applicationUser.firstName} ${applicationUser.lastName}`,
      role: applicationUser.talkJsRole,
      photoUrl: applicationUser.avatar,
      locale: this.getTalkJsLocale(userLanguage),
    });
  }

  async getOrCreateConversation(
    conversationId: string,
    conversationTitle: string,
  ): Promise<Talk.ConversationBuilder | null> {
    const session = await this.currentSessionDeferred.promise;
    if (session.isAlive) {
      const conversationBuilder = session.getOrCreateConversation(conversationId);
      conversationBuilder.setAttributes({
        subject: conversationTitle,
        custom: { type: TALKJS_CONFIGURATION_SETTINGS.conversationType },
      });
      return conversationBuilder;
    }
    return null;
  }

  async destroyTalkJsSession(): Promise<void> {
    const session = await this.getTalkJsSession();
    if (session) {
      await session.clearPushRegistrations();
      session.destroy();
      this.currentSessionDeferred = new Deferred<Talk.Session>();
    }
  }

  private getTalkJsLocale(language: SupportedLanguage | null): string | null {
    if (!language) {
      return null;
    }

    return TALKJS_LOCALE_MAP[language] ?? language;
  }
}
